import { useCallback, useEffect, useRef } from "react";
import {
  View,
  StyleSheet,
  Platform,
  Animated,
  useWindowDimensions,
  type TextStyle,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { ArrowRight } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { GlassButton, InviteGlassCard } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import type { RootStackParamList } from "../../App";
import { GradientOrb } from "@src/components/ui/GradientOrb";
import { useOnboarding } from "../../onboarding/OnboardingContext";
import { useFlowAnalytics } from "@src/packages/analytics/useFlowAnalytics";
import { INVITE_ACCEPT_FLOW } from "@src/packages/analytics/flows";

type Props = NativeStackScreenProps<RootStackParamList, "PartnerInvite">;

const BG_DRIFT_DURATION = 10000;

// React Native's TextStyle / ViewStyle don't include the web-only CSS
// properties we use for the partner-invite shimmer. Extending each with the
// extra fields lets us write the styles with full type-checking and no type
// suppressions, while still being structurally assignable back to the base
// style types when consumed by Animated.Text / View.
type TextStyleWithWebExtras = TextStyle & {
  backgroundImage?: string;
  WebkitBackgroundClip?: string;
  WebkitTextFillColor?: string;
  backgroundClip?: string;
  backgroundSize?: string;
  animation?: string;
};

const DARK = {
  text: "#FFFFFF",
  textMid: "rgba(255, 255, 255, 0.6)",
  textFaint: "rgba(255, 255, 255, 0.35)",
  error: "#F87171",
  green: "#4ADE80",
};

export default function PartnerInviteScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const { width: viewportWidth } = useWindowDimensions();
  const isCompact = viewportWidth < 600;
  const invitationCode = route.params?.code?.trim();
  const hasInvitationCode = Boolean(invitationCode);
  const { beginPartnerInvite } = useOnboarding();
  const inviteFlow = useFlowAnalytics(INVITE_ACCEPT_FLOW);

  // Landing is the top of this funnel and the only place a broken link shows
  // up: someone who arrives without a usable code never reaches onboarding,
  // so nothing downstream would ever record them.
  const landingCapturedRef = useRef(false);
  useEffect(() => {
    if (landingCapturedRef.current) return;
    landingCapturedRef.current = true;
    inviteFlow.started({ has_code: hasInvitationCode });
    inviteFlow.stepViewed("landing", { has_code: hasInvitationCode });
  }, [inviteFlow, hasInvitationCode]);

  // Background drift
  const bgPan = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(bgPan, {
          toValue: 1,
          duration: BG_DRIFT_DURATION,
          useNativeDriver: true,
        }),
        Animated.timing(bgPan, {
          toValue: 0,
          duration: BG_DRIFT_DURATION,
          useNativeDriver: true,
        }),
      ]),
    ).start();
  }, [bgPan]);

  const bgTranslateX = bgPan.interpolate({
    inputRange: [0, 1],
    outputRange: [-30, 30],
  });
  const bgTranslateY = bgPan.interpolate({
    inputRange: [0, 1],
    outputRange: [-15, 15],
  });
  const bgScale = bgPan.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [1.1, 1.16, 1.1],
  });

  // Staggered entrance animations
  const orbOpacity = useRef(new Animated.Value(0)).current;
  const orbScale = useRef(new Animated.Value(1.2)).current;
  const cardOpacity = useRef(new Animated.Value(0)).current;
  const cardTranslateY = useRef(new Animated.Value(40)).current;
  const titleOpacity = useRef(new Animated.Value(0)).current;
  const titleTranslateY = useRef(new Animated.Value(16)).current;
  const bodyOpacity = useRef(new Animated.Value(0)).current;
  const bodyTranslateY = useRef(new Animated.Value(16)).current;
  const ctaOpacity = useRef(new Animated.Value(0)).current;
  const ctaTranslateY = useRef(new Animated.Value(16)).current;

  const animateEntrance = useCallback(() => {
    Animated.parallel([
      Animated.timing(orbOpacity, {
        toValue: 1,
        duration: 600,
        useNativeDriver: true,
      }),
      Animated.timing(orbScale, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
    ]).start();

    setTimeout(() => {
      Animated.parallel([
        Animated.timing(cardOpacity, {
          toValue: 1,
          duration: 700,
          useNativeDriver: true,
        }),
        Animated.timing(cardTranslateY, {
          toValue: 0,
          duration: 700,
          useNativeDriver: true,
        }),
      ]).start();
    }, 400);

    setTimeout(() => {
      Animated.parallel([
        Animated.timing(titleOpacity, {
          toValue: 1,
          duration: 600,
          useNativeDriver: true,
        }),
        Animated.timing(titleTranslateY, {
          toValue: 0,
          duration: 600,
          useNativeDriver: true,
        }),
      ]).start();
    }, 800);

    setTimeout(() => {
      Animated.parallel([
        Animated.timing(bodyOpacity, {
          toValue: 1,
          duration: 600,
          useNativeDriver: true,
        }),
        Animated.timing(bodyTranslateY, {
          toValue: 0,
          duration: 600,
          useNativeDriver: true,
        }),
      ]).start();
    }, 1100);

    setTimeout(() => {
      Animated.parallel([
        Animated.timing(ctaOpacity, {
          toValue: 1,
          duration: 600,
          useNativeDriver: true,
        }),
        Animated.timing(ctaTranslateY, {
          toValue: 0,
          duration: 600,
          useNativeDriver: true,
        }),
      ]).start();
    }, 1400);
  }, [
    orbOpacity,
    orbScale,
    cardOpacity,
    cardTranslateY,
    titleOpacity,
    titleTranslateY,
    bodyOpacity,
    bodyTranslateY,
    ctaOpacity,
    ctaTranslateY,
  ]);

  // Animate on mount
  useEffect(() => {
    animateEntrance();
  }, [animateEntrance]);

  // Stash only the opaque invitation code. URL display metadata is not an
  // authority and must not be carried into onboarding or shown as verified.
  // The linked partner name comes from the API after acceptance.
  const handleGetStarted = useCallback(() => {
    if (!invitationCode) return;
    inviteFlow.stepSubmitted("landing");
    beginPartnerInvite({
      code: invitationCode,
      receivedAt: Date.now(),
    });
    navigation.navigate("Onboarding", { screen: "Sex" });
  }, [invitationCode, inviteFlow, beginPartnerInvite, navigation]);

  const titleText = !hasInvitationCode
    ? t("partner_invite_landing.link_is_incomplete")
    : t("partner_invite_landing.title_default");

  const content: React.ReactNode = (() => {
    return (
      <View style={{ alignItems: "center" }}>
        {/* Eyebrow */}
        <Animated.Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            fontWeight: "600",
            letterSpacing: 1.5,
            textTransform: "uppercase",
            color: DARK.textFaint,
            marginBottom: 28,
            opacity: titleOpacity,
            transform: [{ translateY: titleTranslateY }],
          }}
        >
          {t("partner_invite_landing.eyebrow")}
        </Animated.Text>

        {/* Big title — Tobias font + gradient text on web */}
        <Animated.Text
          style={
            {
              fontSize: 42,
              fontFamily: "Tobias-Thin",
              color: DARK.text,
              textAlign: "center",
              lineHeight: 50,
              letterSpacing: -1,
              marginBottom: 28,
              opacity: titleOpacity,
              transform: [{ translateY: titleTranslateY }],
              ...(Platform.OS === "web" && {
                backgroundImage:
                  "linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.6) 40%, rgba(212,165,116,0.9) 60%, rgba(255,255,255,0.8) 80%, rgba(255,255,255,1) 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                backgroundSize: "200% 200%",
                animation: "inviteTitleShimmer 6s ease-in-out infinite",
              }),
            } satisfies TextStyleWithWebExtras
          }
        >
          {titleText}
        </Animated.Text>

        {/* Body */}
        <Animated.Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: DARK.textMid,
            textAlign: "center",
            lineHeight: 26,
            marginBottom: 48,
            opacity: bodyOpacity,
            transform: [{ translateY: bodyTranslateY }],
          }}
        >
          {!hasInvitationCode
            ? t("partner_invite_landing.ask_partner_to_resend")
            : t("partner_invite_landing.body_default")}
        </Animated.Text>

        {hasInvitationCode && (
          /* CTA button — shimmer border */
          <Animated.View
            style={{
              width: "100%",
              opacity: ctaOpacity,
              transform: [{ translateY: ctaTranslateY }],
            }}
          >
            <GlassButton
              variant="shimmer"
              label={t("partner_invite_landing.get_started")}
              onPress={handleGetStarted}
              trailing={
                <ArrowRight size={16} color="#ffffff" strokeWidth={2} />
              }
              style={{ width: "100%" }}
            />
          </Animated.View>
        )}
      </View>
    );
  })();

  return (
    <View style={styles.root}>
      {/* Dark fabric background with drift */}
      <Animated.Image
        source={require("../../assets/images/beta-invite-bg.avif")}
        style={[
          StyleSheet.absoluteFill,
          {
            width: "120%",
            height: "120%",
            left: "-10%",
            top: "-10%",
            transform: [
              { translateX: bgTranslateX },
              { translateY: bgTranslateY },
              { scale: bgScale },
            ],
          },
        ]}
        resizeMode="cover"
      />

      {/* Dark overlay */}
      <View
        style={[
          StyleSheet.absoluteFill,
          { backgroundColor: "rgba(0, 0, 0, 0.35)" },
        ]}
      />

      <SafeAreaView style={{ flex: 1 }}>
        <View style={[styles.wrapper, isCompact && styles.wrapperCompact]}>
          {/* Orb — floats above the card, animates first */}
          <Animated.View
            style={{
              alignItems: "center",
              marginBottom: -48,
              zIndex: 10,
              opacity: orbOpacity,
              transform: [{ scale: orbScale }],
            }}
          >
            <GradientOrb size={96} />
          </Animated.View>

          <InviteGlassCard
            style={{
              opacity: cardOpacity,
              transform: [{ translateY: cardTranslateY }],
            }}
          >
            {content}
          </InviteGlassCard>
        </View>
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: "100%",
    height: "100%",
    overflow: "hidden",
    backgroundColor: "#0A0A0A",
  },
  wrapper: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 20,
  },
  wrapperCompact: {
    justifyContent: "flex-end",
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
});
